// pages/UserProfilePage.jsx
import React from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  Skeleton,
  Avatar,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2"; // Grid2 estable (MUI v6)
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EditIcon from "@mui/icons-material/Edit";
import { useParams, useOutletContext } from "react-router-dom";
import { useApiManager } from "lib/ApiContext";
import AvatarUploadDialog from "./AvatarUploadDialog";


const purple = "#9D00FF";
const cyan = "#6A00CC";

/* ====== CONSTS para posición ====== */
const AVATAR_SIZE = 110;     // diámetro del avatar
const AVATAR_LEFT = 32;      // margen izquierdo del avatar
const GUTTER = 16;           // espacio entre avatar y textos
const NAME_LEFT = AVATAR_LEFT + AVATAR_SIZE + GUTTER; // ← posición del bloque de textos

/* ====== Avatar "editable" (solo UI) ====== */
function EditableAvatar({ src, alt, initials = "U", onClick }) {
  return (
    <Box
      sx={{
        position: "absolute",
        bottom: -AVATAR_SIZE / 2,
        left: AVATAR_LEFT,
        width: AVATAR_SIZE,
        height: AVATAR_SIZE,
        borderRadius: "50%",
        overflow: "none",
        border: "4px solid #0A0A12",
        cursor: "pointer",
        boxShadow: 3,
        "&:hover .ea-overlay": { opacity: 1 },
      }}
      onClick={onClick}
      aria-label="Change profile photo"
      role="button"
    >
      <Avatar
        src={src || undefined}
        alt={alt}
        sx={{
          width: "100%",
          height: "100%",
          fontSize: 36,
          fontWeight: 800,
          bgcolor: "#14141F",
        }}
      >
        {initials}
      </Avatar>

      {/* Overlay circular (sin bordes cuadrados) */}
      <Box
        className="ea-overlay"
        sx={{
          position: "absolute",
          inset: 0,
          background: "rgba(0,0,0,0.55)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          color: "#EDE9F7",
          fontWeight: 700,
          opacity: 0,
          transition: "opacity 0.25s ease",
          borderRadius: "50%",                 // <- clip circular
          clipPath: "circle(50% at 50% 50%)",  // <- refuerzo para evitar cuadro
          userSelect: "none",
          pointerEvents: "none",
          textAlign: "center",
        }}
      >
        <EditIcon sx={{ color: "#C77DFF", fontSize: 28, mb: 0.5 }} />
        <Typography variant="body2" sx={{ lineHeight: 1 }}>
          Change
        </Typography>
      </Box>
    </Box>
  );
}

const initialsOf = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s?.[0]?.toUpperCase())
    .join("") || "U";

export default function UserProfileEditPage({ onSave }) {
  const { username } = useParams();
  const api = useApiManager();

  const [open, setOpen] = React.useState(false);
  const [user, setUser] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState(null);
  const [dirty, setDirty] = React.useState(true);
  const {
      setAvatarDirty,
      errorToShow
    } = useOutletContext() || {};

  const handleSave = async (blob, fileName) => {
    // Aquí subes el blob a S3 con presigned URL y guardas el key en Dynamo
    // Ejemplo rápido:
    const { uploadUrl, key } = await api.GetAvatarUploadUrl({ fileName, contentType: "image/png" });
    await fetch(uploadUrl, { method: "PUT", headers: { "Content-Type": "image/png" }, body: blob });
    // await api.UpdateUserAvatar({ username, avatarKey: key });
    setDirty(!dirty);
    setAvatarDirty(true);
  };

  const handleRemove = async () => {
    // Lógica para borrar/quitar el avatar del usuario
    // await api.UpdateUserAvatar({ username, avatarKey: null })
  };
  
  const load = React.useCallback(async () => {
    if (!api || !username) return;
    setLoading(true);
    setErr(null);
    try {
      const data = await api.GetUserData(username);
      setUser(data);
    } catch (e) {
      setErr(e?.message || "No se pudo cargar el perfil.");
    } finally {
      setLoading(false);
    }
  }, [api, username, dirty]);

  React.useEffect(() => {
    load();
  }, [load]);

  if (loading || !user) {
    return (
      <Box display="flex" justifyContent="center" sx={{ mt: 6 }}>
        <Skeleton variant="rectangular" height={200} width="80%" sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  const name = user.UserName || username;

  return (
    <Box sx={{ color: "#EDE9F7", mt: 4}}>
      {/* Header */}
      <Box
        sx={{
          height: { xs: 140, sm: 180, md: 200 },
          width: "100%",
          borderRadius: 2,
          mb: 6,
          position: "relative",
          overflow: "none",
          background: "#1E1B2E",
        }}
      >
        {/* sombreado inferior */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(0,0,0,0) 45%, rgba(12,8,20,0.9) 100%)",
          }}
        />
         {/* Avatar editable */}
        <EditableAvatar
          src={user.SignedUrl /* si lo agregas */}
          alt={name}
          initials={initialsOf(name)}
          onClick={() => {
            // Aquí abrirías el file picker para subir a S3
            setOpen(true);
          }}
        />
        <AvatarUploadDialog
        open={open}
        onClose={() => setOpen(false)}
        onSave={handleSave}
        onRemove={handleRemove}
        initialImageUrl={user.SignedUrl}
      />

        {/* Bloque de textos a la DERECHA del avatar (sin overlap) */}
        <Box
          sx={{
            position: "absolute",
            bottom: 8,
            left: `${NAME_LEFT}px`, // ← evita el solape con el avatar
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 26, sm: 32, md: 36 },
              color: "#EDE9F7",
              fontWeight: 800,
              textShadow: `
                -1px -1px 0 ${purple},
                 1px -1px 0 ${purple},
                -1px  1px 0 ${purple},
                 1px  1px 0 ${purple}
              `,
              mb: 0.5,
            }}
          >
            {name}
          </Typography>
          <Typography variant="body2" sx={{ color: "#EDE9F7", position: "relative", top: -6 }}>
            @{name}
          </Typography>
        </Box>
      </Box>

      {/* Caja de edición centrada */}
      <Box sx={{ display: "flex", justifyContent: "center"}}>
        <Box sx={{ maxWidth: 800, mb: 8 }}>
          <Grid2 container spacing={3}>
            <Grid2 xs={12}>
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: "10px",
                  bgcolor: "#14141F",
                  border: "1px solid rgba(157,0,255,0.2)",
                  boxShadow: "0 8px 30px rgba(0,0,0,0.35)",
                }}
              >
                {/* Username (no editable) */}
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 800, mb: 0.5, color: "#EDE9F7", textAlign: "center" }}
                >
                  Username
                </Typography>
                <TextField
                  fullWidth
                  value={name}
                  disabled
                  sx={{
                    mb: 3,
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#EDE9F7",
                    },
                    "& .MuiOutlinedInput-root.Mui-disabled": {
                      backgroundColor: "rgba(255,255,255,0.05)",
                    },
                  }}
                />

                {/* Bio */}
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 800, mb: 0.5, color: "#EDE9F7", textAlign: "center" }}
                >
                  Bio
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  value={user.Bio || ""}
                  onChange={(e) => setUser({ ...user, Bio: e.target.value })}
                  placeholder="Cuéntale al mundo quién eres…"
                  sx={{ mb: 3, textarea: { color: "#EDE9F7" } }}
                />

                {/* Website */}
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 800, mb: 0.5, color: "#EDE9F7", textAlign: "center" }}
                >
                  Website
                </Typography>
                <TextField
                  fullWidth
                  value={user.Website || ""}
                  onChange={(e) => setUser({ ...user, Website: e.target.value })}
                  placeholder="https://tu-sitio.com"
                  sx={{ mb: 3, input: { color: "#EDE9F7" } }}
                />

                {/* Email Notifications */}
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!user.AcceptEmailNotifications}
                      onChange={(_, c) =>
                        setUser((u) => ({ ...u, AcceptEmailNotifications: c }))
                      }
                    />
                  }
                  label="Email notifications"
                  sx={{ mb: 3, color: "#EDE9F7" }}
                />

                <Divider sx={{ my: 2, borderColor: "rgba(237,233,247,0.08)" }} />

                {/* Fecha de registro (opcional) */}
                {user.DateAdded && (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 2, color: "#EDE9F7" }}
                  >
                    <CalendarMonthIcon fontSize="small" />
                    <Typography variant="body2">Joined {user.DateAdded}</Typography>
                  </Stack>
                )}

                {/* Acciones */}
                <Stack direction="row" spacing={1.5}>
                  <Button
                    variant="contained"
                    onClick={() => onSave && onSave(user)}
                    sx={{
                      bgcolor: cyan,
                      color: "#EDE9F7",
                      fontWeight: 800,
                      textTransform: "none",
                      "&:hover": { bgcolor: "#9D00FF" },
                    }}
                  >
                    Save Changes
                  </Button>
                  <Button
                    variant="outlined"
                    sx={{
                      borderColor: purple,
                      color: "#C77DFF",
                      fontWeight: 800,
                      textTransform: "none",
                      "&:hover": { borderColor: "#C77DFF", color: "#C77DFF" },
                    }}
                    onClick={() => window.history.back()}
                  >
                    Cancel
                  </Button>
                </Stack>

                {err && (
                  <Typography color="error" sx={{ mt: 1 }}>
                    {err}
                  </Typography>
                )}
              </Box>
            </Grid2>
          </Grid2>
        </Box>
      </Box>
    </Box>
  );
}
